
import React, { useMemo, useState } from "react";
import {
  ShieldCheck,
  Users,
  Plus,
  Pencil,
  Trash2,
  X,
  Save,
  RotateCcw,
  LockKeyhole,
  ChevronRight,
  Search,
} from "lucide-react";

const permissionModules = [
  {
    name: "Dashboard",
    description: "Dashboard and summary information",
    permissions: ["View"],
  },
  {
    name: "Members",
    description: "Member profiles and registration",
    permissions: ["View", "Add", "Edit", "Delete", "Approve", "Manage Status"],
  },
  {
    name: "Membership Plans",
    description: "Membership packages and pricing",
    permissions: ["View", "Add", "Edit", "Delete", "Manage Status"],
  },
  {
    name: "Payments",
    description: "Payment history and transactions",
    permissions: ["View", "Add", "Edit", "Delete"],
  },
  {
    name: "Reports",
    description: "Reports and business summaries",
    permissions: ["View"],
  },
  {
    name: "Masters",
    description: "Manage dropdown and master data",
    permissions: ["View", "Add", "Edit", "Delete"],
  },
  {
    name: "Staff Management",
    description: "Staff accounts and assigned roles",
    permissions: ["View", "Add", "Edit", "Delete", "Manage Status"],
  },
  {
    name: "Settings",
    description: "Application configuration",
    permissions: ["View", "Edit"],
  },
];

const permissionActions = [
  "View",
  "Add",
  "Edit",
  "Delete",
  "Approve",
  "Manage Status",
];

const defaultRoles = [
  {
    id: 1,
    name: "Super Admin",
    description: "Complete access to all admin modules.",
    system: true,
    permissions: Object.fromEntries(
      permissionModules.map((module) => [
        module.name,
        [...module.permissions],
      ])
    ),
  },
  {
    id: 2,
    name: "Staff Admin",
    description: "Manage members, payments and daily operations.",
    system: true,
    permissions: {
      Dashboard: ["View"],
      Members: ["View", "Add", "Edit", "Approve", "Manage Status"],
      "Membership Plans": ["View"],
      Payments: ["View"],
      Reports: ["View"],
      Masters: ["View"],
      "Staff Management": ["View"],
      Settings: ["View"],
    },
  },
  {
    id: 3,
    name: "Staff",
    description: "Basic access for daily member operations.",
    system: true,
    permissions: {
      Dashboard: ["View"],
      Members: ["View", "Add", "Edit"],
      "Membership Plans": ["View"],
      Payments: ["View"],
      Reports: [],
      Masters: [],
      "Staff Management": [],
      Settings: [],
    },
  },
];

function RoleBadge({ system }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold ${
        system
          ? "bg-blue-50 text-blue-700"
          : "bg-violet-50 text-violet-700"
      }`}
    >
      {system ? <LockKeyhole size={11} /> : <ShieldCheck size={11} />}
      {system ? "System Role" : "Custom Role"}
    </span>
  );
}

function RoleModal({ role, onClose, onSave }) {
  const [name, setName] = useState(role?.name || "");
  const [description, setDescription] = useState(role?.description || "");
  const [error, setError] = useState("");

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!name.trim()) {
      setError("Please enter a role name.");
      return;
    }

    onSave({
      name: name.trim(),
      description: description.trim(),
    });
  };

  const inputClass =
    "mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100";

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/50 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <h2 className="font-bold text-slate-800">
              {role ? "Edit Role" : "Create New Role"}
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              Define the role name and its purpose.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close role form"
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 p-5">
            <div>
              <label className="text-xs font-semibold text-slate-600">
                Role Name *
              </label>
              <input
                required
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setError("");
                }}
                placeholder="e.g. Member Manager"
                className={inputClass}
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600">
                Description
              </label>
              <textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the responsibilities of this role"
                className={inputClass}
              />
            </div>

            {error && (
              <p role="alert" className="text-xs text-rose-600">
                {error}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50 p-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-xs font-semibold text-slate-600"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="rounded-lg bg-blue-600 px-4 py-2.5 text-xs font-semibold text-white hover:bg-blue-700"
            >
              {role ? "Save Changes" : "Create Role"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AssignRoles() {
  const [roles, setRoles] = useState(defaultRoles);
  const [selectedRoleId, setSelectedRoleId] = useState(1);
  const [search, setSearch] = useState("");
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [deleteRole, setDeleteRole] = useState(null);
  const [notice, setNotice] = useState("");
  

  const selectedRole = roles.find((role) => role.id === selectedRoleId);

  const rolePermissions = selectedRole?.permissions || {};

  const filteredRoles = useMemo(() => {
    const term = search.trim().toLowerCase();

    return roles.filter(
      (role) =>
        role.name.toLowerCase().includes(term) ||
        role.description.toLowerCase().includes(term)
    );
  }, [roles, search]);

  const hasPermission = (moduleName, action) =>
    (rolePermissions[moduleName] || []).includes(action);

  const togglePermission = (moduleName, action) => {
    if (!selectedRole || selectedRole.name === "Super Admin") return;

    setRoles((current) =>
      current.map((role) => {
        if (role.id !== selectedRoleId) return role;

        const currentPermissions = role.permissions[moduleName] || [];
        const updatedPermissions = currentPermissions.includes(action)
          ? currentPermissions.filter((item) => item !== action)
          : [...currentPermissions, action];

        return {
          ...role,
          permissions: {
            ...role.permissions,
            [moduleName]: updatedPermissions,
          },
        };
      })
    );
  };

  const toggleModule = (module) => {
    if (!selectedRole || selectedRole.name === "Super Admin") return;

    const allSelected = module.permissions.every((action) =>
      hasPermission(module.name, action)
    );

    setRoles((current) =>
      current.map((role) => {
        if (role.id !== selectedRoleId) return role;

        return {
          ...role,
          permissions: {
            ...role.permissions,
            [module.name]: allSelected ? [] : [...module.permissions],
          },
        };
      })
    );
  };

  const savePermissions = () => {
  
    setNotice(`Permissions saved for ${selectedRole.name}.`);
  };

  const resetPermissions = () => {
    const originalRole = defaultRoles.find(
      (role) => role.id === selectedRoleId
    );

    if (!originalRole) return;

    setRoles((current) =>
      current.map((role) =>
        role.id === selectedRoleId
          ? {
              ...role,
              permissions: JSON.parse(
                JSON.stringify(originalRole.permissions)
              ),
            }
          : role
      )
    );

    setNotice("Permissions reset to the sample defaults.");
  };

  const createOrUpdateRole = (form) => {
    if (editingRole) {
      setRoles((current) =>
        current.map((role) =>
          role.id === editingRole.id
            ? { ...role, ...form }
            : role
        )
      );
      setNotice("Role details updated.");
    } else {
      const newRole = {
        id: Date.now(),
        ...form,
        system: false,
        permissions: {},
      };

      setRoles((current) => [...current, newRole]);
      setSelectedRoleId(newRole.id);
      setNotice("Role created. Configure its permissions below.");
    }

    setRoleModalOpen(false);
    setEditingRole(null);
  };

  const confirmDeleteRole = () => {
    setRoles((current) =>
      current.filter((role) => role.id !== deleteRole.id)
    );

    if (selectedRoleId === deleteRole.id) {
      const nextRole = roles.find((role) => role.id !== deleteRole.id);
      setSelectedRoleId(nextRole?.id || 0);
    }

    setNotice("Custom role removed.");
    setDeleteRole(null);
  };

  const isSuperAdmin = selectedRole?.name === "Super Admin";

  return (
    <div className="space-y-6">
      {/* Page heading */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2 text-[11px] text-slate-400">
            <span>Home</span>
            <span>/</span>
            <span>Staff Management</span>
            <span>/</span>
            <span className="font-medium text-blue-600">Assign Roles</span>
          </div>

          <h1 className="text-xl font-bold text-slate-800 sm:text-2xl">
            Roles & Permissions
          </h1>

          <p className="mt-1 text-xs text-slate-500 sm:text-sm">
            Manage staff roles and control access to admin modules.
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            setEditingRole(null);
            setRoleModalOpen(true);
          }}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-xs font-semibold text-white shadow-sm hover:bg-blue-700"
        >
          <Plus size={16} />
          Create New Role
        </button>
      </div>

      {notice && (
        <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          <span>{notice}</span>
          <button
            type="button"
            onClick={() => setNotice("")}
            aria-label="Dismiss message"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">Total Roles</p>
              <h3 className="mt-2 text-2xl font-bold text-slate-800">
                {roles.length}
              </h3>
            </div>
            <div className="rounded-xl bg-blue-50 p-3 text-blue-600">
              <ShieldCheck size={22} />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">System Roles</p>
              <h3 className="mt-2 text-2xl font-bold text-slate-800">
                {roles.filter((role) => role.system).length}
              </h3>
            </div>
            <div className="rounded-xl bg-violet-50 p-3 text-violet-600">
              <LockKeyhole size={22} />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">Custom Roles</p>
              <h3 className="mt-2 text-2xl font-bold text-slate-800">
                {roles.filter((role) => !role.system).length}
              </h3>
            </div>
            <div className="rounded-xl bg-emerald-50 p-3 text-emerald-600">
              <Users size={22} />
            </div>
          </div>
        </div>
      </div>

      {/* Role selection and permissions */}
      <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[280px_minmax(0,1fr)]">
        {/* Role list */}
        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 p-4">
            <h2 className="text-sm font-bold text-slate-800">Available Roles</h2>
            <p className="mt-1 text-[11px] text-slate-400">
              Select a role to manage permissions.
            </p>

            <div className="relative mt-4">
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search roles"
                className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-3 text-xs outline-none focus:border-blue-400"
              />
            </div>
          </div>

          <div className="space-y-2 p-3">
            {filteredRoles.map((role) => {
              const selected = selectedRoleId === role.id;

              return (
                <button
                  key={role.id}
                  type="button"
                  onClick={() => setSelectedRoleId(role.id)}
                  className={`w-full rounded-xl border p-3 text-left transition ${
                    selected
                      ? "border-blue-200 bg-blue-50/70"
                      : "border-transparent hover:border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                        selected
                          ? "bg-blue-100 text-blue-600"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      <ShieldCheck size={19} />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="truncate text-xs font-semibold text-slate-800">
                          {role.name}
                        </p>
                        {selected && (
                          <ChevronRight
                            size={15}
                            className="shrink-0 text-blue-600"
                          />
                        )}
                      </div>

                      <p className="mt-1 line-clamp-2 text-[10px] leading-4 text-slate-500">
                        {role.description || "No description provided."}
                      </p>

                      <div className="mt-2">
                        <RoleBadge system={role.system} />
                      </div>
                    </div>
                  </div>

                  {!role.system && (
                    <div className="mt-3 flex justify-end gap-1 border-t border-slate-200/70 pt-2">
                      <span
                        onClick={(event) => {
                          event.stopPropagation();
                          setEditingRole(role);
                          setRoleModalOpen(true);
                        }}
                        role="button"
                        tabIndex={0}
                        className="rounded-md p-1.5 text-amber-600 hover:bg-amber-50"
                        title="Edit role"
                      >
                        <Pencil size={14} />
                      </span>

                      <span
                        onClick={(event) => {
                          event.stopPropagation();
                          setDeleteRole(role);
                        }}
                        role="button"
                        tabIndex={0}
                        className="rounded-md p-1.5 text-rose-600 hover:bg-rose-50"
                        title="Delete role"
                      >
                        <Trash2 size={14} />
                      </span>
                    </div>
                  )}
                </button>
              );
            })}

            {filteredRoles.length === 0 && (
              <p className="px-3 py-8 text-center text-xs text-slate-400">
                No roles found.
              </p>
            )}
          </div>
        </section>

        {/* Permission matrix */}
        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {selectedRole ? (
            <>
              <div className="flex flex-col gap-4 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                    <ShieldCheck size={22} />
                  </div>

                  <div>
                    <h2 className="text-sm font-bold text-slate-800">
                      {selectedRole.name} Permissions
                    </h2>
                    <p className="mt-1 text-[11px] text-slate-500">
                      Configure module-level access for this role.
                    </p>
                  </div>
                </div>

                <RoleBadge system={selectedRole.system} />
              </div>

              {isSuperAdmin && (
                <div className="m-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 sm:m-5">
                  <LockKeyhole size={16} className="mt-0.5 shrink-0" />
                  <p>
                    Super Admin has full access to all modules. Its
                    permissions are protected in this UI.
                  </p>
                </div>
              )}

              <div className="p-4 sm:p-5">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold text-slate-700">
                      Module Permissions
                    </p>
                    <p className="mt-1 text-[10px] text-slate-400">
                      Select the actions this role is allowed to perform.
                    </p>
                  </div>

                  <span className="rounded-full bg-slate-100 px-3 py-1 text-[10px] font-medium text-slate-600">
                    {permissionModules.length} Modules
                  </span>
                </div>

                <div className="space-y-3">
                  {permissionModules.map((module) => {
                    const availableActions = permissionActions.filter(
                      (action) => module.permissions.includes(action)
                    );

                    const allSelected =
                      availableActions.length > 0 &&
                      availableActions.every((action) =>
                        hasPermission(module.name, action)
                      );

                    return (
                      <div
                        key={module.name}
                        className="overflow-hidden rounded-xl border border-slate-200"
                      >
                        <div className="flex flex-col gap-3 bg-slate-50/70 p-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
                          <div>
                            <h3 className="text-xs font-semibold text-slate-800">
                              {module.name}
                            </h3>
                            <p className="mt-1 text-[10px] text-slate-500">
                              {module.description}
                            </p>
                          </div>

                          <label className="inline-flex cursor-pointer items-center gap-2 self-start text-[10px] font-medium text-slate-600 sm:self-center">
                            <input
                              type="checkbox"
                              checked={allSelected}
                              disabled={isSuperAdmin}
                              onChange={() => toggleModule(module)}
                              className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                            />
                            Select all
                          </label>
                        </div>

                        <div className="grid grid-cols-2 gap-3 p-3 sm:grid-cols-3 lg:grid-cols-4">
                          {availableActions.map((action) => (
                            <label
                              key={action}
                              className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2.5 text-[11px] transition ${
                                hasPermission(module.name, action)
                                  ? "border-blue-200 bg-blue-50/70 text-blue-700"
                                  : "border-slate-200 text-slate-600 hover:bg-slate-50"
                              } ${
                                isSuperAdmin
                                  ? "cursor-not-allowed opacity-80"
                                  : ""
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={hasPermission(module.name, action)}
                                disabled={isSuperAdmin}
                                onChange={() =>
                                  togglePermission(module.name, action)
                                }
                                className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                              />
                              {action}
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-5 flex flex-col-reverse gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
                  <button
                    type="button"
                    onClick={resetPermissions}
                    disabled={isSuperAdmin}
                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 px-4 py-2.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <RotateCcw size={14} />
                    Reset Permissions
                  </button>

                  <button
                    type="button"
                    onClick={savePermissions}
                    disabled={isSuperAdmin}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Save size={15} />
                    Save Permissions
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="p-12 text-center">
              <ShieldCheck
                size={30}
                className="mx-auto text-slate-300"
              />
              <p className="mt-3 text-sm font-semibold text-slate-700">
                Select a role
              </p>
              <p className="mt-1 text-xs text-slate-400">
                Choose a role from the list to configure permissions.
              </p>
            </div>
          )}
        </section>
      </div>

      {/* Create / Edit role modal */}
      {roleModalOpen && (
        <RoleModal
          role={editingRole}
          onClose={() => {
            setRoleModalOpen(false);
            setEditingRole(null);
          }}
          onSave={createOrUpdateRole}
        />
      )}

      {/* Delete confirmation */}
      {deleteRole && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 text-rose-600">
              <Trash2 size={22} />
            </div>

            <h3 className="mt-4 text-center text-lg font-bold text-slate-800">
              Delete Role?
            </h3>

            <p className="mt-2 text-center text-sm text-slate-500">
              Are you sure you want to delete{" "}
              <strong>{deleteRole.name}</strong>?
            </p>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setDeleteRole(null)}
                className="flex-1 rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={confirmDeleteRole}
                className="flex-1 rounded-lg bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-rose-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
